import { DateTime } from 'luxon'
import {
  getProductName,
  getTransactionType,
} from 'components/transaction-summary/transaction-utils'

export const PAYMENT_STATUS = {
  COMPLETED: 'COMPLETED',
  PENDING: 'PENDING',
  REFUNDED: 'REFUNDED',
}

export const PAYMENT_STATUS_LABEL = {
  [PAYMENT_STATUS.COMPLETED]: 'Completed',
  [PAYMENT_STATUS.PENDING]: 'Pending',
  [PAYMENT_STATUS.REFUNDED]: 'Refunded',
}

export const getPayorName = payor =>
  payor?.name || `${payor?.firstName || ''} ${payor?.lastName || ''}`.trim()

export const isOnlinePaymentSourceType = sourceType =>
  Boolean(
    sourceType && ['stripe', 'card'].includes(String(sourceType).toLowerCase()),
  )

export const hasOnlineRefundSupport = payment => {
  const { transactions = [], cardBrand, last4 } = payment
  const primaryTransaction = transactions.find(
    transaction => !transaction.refund,
  )

  if (primaryTransaction?.sourceType) {
    return isOnlinePaymentSourceType(primaryTransaction.sourceType)
  }

  return Boolean(cardBrand && last4)
}

export const canShowOnlineRefund = ({
  transactions = [],
  cardBrand,
  last4,
  leftToRefundInCents = 0,
  productTypeName,
  isCoach = true,
}) => {
  const isRefundableProduct =
    !productTypeName ||
    ['Enrollment', 'LessonPack', 'PracticeSession'].includes(productTypeName)

  return (
    isCoach &&
    isRefundableProduct &&
    leftToRefundInCents > 0 &&
    hasOnlineRefundSupport({ transactions, cardBrand, last4 })
  )
}

export const formatRefundErrorMessage = error => {
  if (!error) return ''

  const graphQLError = error.graphQLErrors?.[0]?.message
  if (graphQLError) return graphQLError

  const message = error.message || ''

  if (/status code 5\d\d/i.test(message)) {
    return 'We could not process this refund right now. Please try again or contact support.'
  }

  if (/failed to fetch|network/i.test(message)) {
    return 'Unable to reach the server. Check your connection and try again.'
  }

  return message || 'Unable to process refund. Please try again.'
}

export const getPaymentStatus = payment => {
  const { transactions = [] } = payment
  const hasRefund = transactions.some(transaction => transaction.refund)

  if (hasRefund) {
    return PAYMENT_STATUS.REFUNDED
  }

  return PAYMENT_STATUS.COMPLETED
}

const getRefundTotalInCents = transactions =>
  transactions
    .filter(transaction => transaction.refund)
    .reduce((sum, transaction) => sum + transaction.amountInCents, 0)

export const getNetIncomeInCents = payment => {
  const { transactions = [], totalInCents, leftToRefundInCents } = payment

  if (typeof leftToRefundInCents === 'number') {
    return Math.max(0, leftToRefundInCents)
  }

  const purchaseTotal = transactions
    .filter(transaction => !transaction.refund)
    .reduce((sum, transaction) => sum + transaction.amountInCents, 0)
  const refundTotal = getRefundTotalInCents(transactions)

  if (transactions.length > 0) {
    return Math.max(0, purchaseTotal - refundTotal)
  }

  return getPaymentStatus(payment) === PAYMENT_STATUS.REFUNDED
    ? 0
    : totalInCents
}

export const getDisplayAmountInCents = payment => {
  const { transactions = [], totalInCents } = payment
  const refundTotal = getRefundTotalInCents(transactions)

  if (refundTotal > 0) {
    return -refundTotal
  }

  if (getPaymentStatus(payment) === PAYMENT_STATUS.REFUNDED) {
    return -totalInCents
  }

  return totalInCents
}

export const formatTransactionDate = createdAt => {
  const date = DateTime.fromISO(createdAt)
  if (!date.isValid) return ''

  return date.toFormat('LLL d, yyyy · h:mm a')
}

export const formatMonthRange = selectedMonth => {
  const start = selectedMonth.startOf('month')
  const end = selectedMonth.endOf('month')

  return `${start.toFormat('LLL d')} – ${end.toFormat('LLL d, yyyy')}`
}

export const getTransactionDescription = payment => {
  const product = payment.products?.[0]
  if (!product) return ''

  const productName = getProductName(product) || ''
  const transactionType = getTransactionType(product)

  if (
    transactionType &&
    productName &&
    !productName.includes(transactionType)
  ) {
    return `${transactionType} - ${productName}`
  }

  return productName
}

export const getCompletedIncomeInCents = payments =>
  payments.reduce((sum, payment) => sum + getNetIncomeInCents(payment), 0)

export const formatSignedAmount = amountInCents => {
  const dollars = Math.abs(amountInCents) / 100
  const formatted = Number.isInteger(dollars)
    ? dollars.toString()
    : dollars.toFixed(2)
  const prefix = amountInCents >= 0 ? '+' : '-'

  return `${prefix}$${formatted}`
}

export const formatTransactionDateForCsv = createdAt => {
  const date = DateTime.fromISO(createdAt)
  if (!date.isValid) return ''

  return date.toFormat('LLL d, yyyy h:mm a')
}

export const formatAmountForCsv = amountInCents =>
  (amountInCents / 100).toFixed(2)

export const getTransactionsExportFilename = selectedMonth =>
  `transactions_${selectedMonth.toFormat('yyyy-MM')}.csv`

export const generateTransactionsCsvData = payments =>
  payments.map(payment => ({
    Name: getPayorName(payment.payor),
    Status: PAYMENT_STATUS_LABEL[getPaymentStatus(payment)],
    Description: getTransactionDescription(payment),
    Date: formatTransactionDateForCsv(payment.createdAt),
    Amount: formatAmountForCsv(getDisplayAmountInCents(payment)),
    'Transaction ID': payment.id,
  }))
